/**
 * @file PowerBIAuth.js
 * @module PowerBIAuth
 * @description Power BI の埋め込み用トークン・URLの取得と更新を行うモジュール
 */

import 'react-toastify/dist/ReactToastify.css';
import { logToCloudWatch } from './Rum-init';
import { SC_STATE , C_URLS , C_LOGWORDING , C_ERRWORDING } from './Constants';

//const BITOKEN_API_URL = 'https://wzg9ogh7ei.execute-api.ap-northeast-1.amazonaws.com/dev/credential/bitoken';
//const BITOKEN_API_URL = 'https://api.web.hydra-dev.alap-cloud.net/credential/bitoken';

/**
 * 共通コンソールトークンを基にAPI経由でバックエンドへPower BI 認証処理を依頼し、応答結果として
 * Power BI の埋め込み用トークン・URLを取得します。呼び出し元へはコールバックおよび戻り値で返却します。
 *
 * @async
 * @param {string} cnsltokenRef - 共通コンソールから取得したJWTトークン
 * @param {function} [setEmbedData] - トークン・埋め込み情報・有効期限を受け取るコールバック関数（省略可）
 * @param {function} [setLoadingState] - ローディング状態を更新するコールバック関数（省略可）
 * @param {function} [setError] - エラー情報を更新するコールバック関数（省略可）
 * @returns {{bitoken: string, embedURL: string, expiration: string}} 埋め込み用トークン・URL・有効期限のオブジェクト
 * @throws {Error} API通信エラーや認証エラー時は例外をスロー
 */
export const PowerBIAuth = async (cnsltokenRef, setEmbedData, setLoadingState, setError) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);
  //console.log('[PowerBIAuth][cnsltokenRef]token=\n', cnsltokenRef.current);

  try {
    // 画面状態のセッター関数ありの場合、BIトークン取得中へ状態を変更
    setLoadingState?.(SC_STATE.BI_TOKEN_FETCHING);
    // BIトークンの取得
    const response = await fetch(C_URLS.BITOKEN_API, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${cnsltokenRef.current}`,
      },
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    // BIトークンの取得時、エラー応答を受信した場合
    if (!response.ok) {
      logToCloudWatch('[PowerBIAuth] HTTPステータスコードが200以外','FRONTEND_ERROR','CRITICAL');
      setLoadingState?.(SC_STATE.ERR_NONPERMANENT);
      const error = new Error(`HTTP error: ${response.status}`);
      error.status = response.status;
      error.statusText = response.statusText;
      throw error;
    }

    const data = await response.json();
    // 取得したEmbed TokenとURLをApp.jsに渡し、認証完了状態とする
    setEmbedData?.(data.token, data.embedUrl, data.expiration);
    setLoadingState?.(SC_STATE.AUTHENTICATED);

    // トークンの有効期限が近づいたら再取得を行うタイマーを設定
    setBiTokenRefreshTimer(data.expiration, async () => {
      try {
        await PowerBIAuth(cnsltokenRef, setEmbedData, setLoadingState, setError);
      } catch (error) {
        setError({
          errorCode: error.status || 'UNKNOWN',
          errorMessage: error.statusText || error.message || '不明なエラー',
          detailedMessage: error.stack || '',
        });
        setLoadingState?.(SC_STATE.ERR_NONPERMANENT);
      }
    });
    // 戻り値としても、Enbed TokenとURLを返す
    return {
      bitoken: data.token,
      embedURL: data.embedUrl,
      expiration: data.expiration,
    };
  } catch (error) {
    // セッター設定ありならば、エラー内容はApp.jsへ渡す。それ以外エラー処理としては、CloudWatchへログを出力
    if (typeof setError === 'function') {
      setError({
        errorCode: error.status || 'UNKNOWN',
        errorMessage: error.statusText || error.message || '不明なエラー',
        detailedMessage: error.stack || '',
      });
    }
    if (error.name === 'AbortError') {
      logToCloudWatch('[PowerBIAuth]APIタイムアウト（30秒）','FRONTEND_ERROR','CRITICAL');
      setLoadingState?.(SC_STATE.ERR_NONPERMANENT);
    } else if (error.status) {
      const status = error.status;
      let logMessage = `[PowerBIAuth] HTTPエラー: ${status}`;
      let errorType = SC_STATE.ERR_NONPERMANENT;

      switch (status) {
        case 401:
          logMessage = '[PowerBIAuth] 認可情報無効（401）';
          errorType = SC_STATE.ERR_NONPERMANENT;
          break;
        case 403:
          logMessage = '[PowerBIAuth] 認可不可（403）';
          errorType = SC_STATE.ERR_NONPERMANENT;
          break;
        case 404:
          logMessage = '[PowerBIAuth] 指定リソースが存在しない（404）';
          errorType = SC_STATE.ERR_PERMANENT;
          break;
        case 500:
          logMessage = '[PowerBIAuth] 内部エラー（500）';
          errorType = SC_STATE.ERR_PERMANENT;
          break;
        case 504:
          logMessage = '[PowerBIAuth] タイムアウト（504）';
          errorType = SC_STATE.ERR_NONPERMANENT;
          break;
        default:
          logMessage = `[PowerBIAuth] その他HTTPステータスコード（${status}）`;
          errorType = SC_STATE.ERR_NONPERMANENT;
          break;
      }

      logToCloudWatch(logMessage, 'FRONTEND_ERROR', 'CRITICAL');
      setLoadingState?.(errorType);
    } else {
      logToCloudWatch('[PowerBIAuth] HTTPステータスコード無しのエラー', 'FRONTEND_ERROR', 'CRITICAL');
      setLoadingState?.(SC_STATE.ERR_NONPERMANENT);
    }
    //console.error('[PowerBIAuth][throw]', error);
    throw error;
  }
};

let refreshTimeoutRef = null;

/**
 * トークンの有効期限に基づいて、タイマー時刻に第二引数を発火する。
 *
 * @function setBiTokenRefreshTimer
 * @param {string} expiration - トークンの有効期限（ISO 8601 形式の文字列）。
 * @param {string} onRefresh - 有効期限の10分前に発火したいコールバック）。
 * @returns {void}
 */
export const setBiTokenRefreshTimer = (expiration, onRefresh) => {
  const expirationTime = new Date(expiration).getTime();
  const refreshTime = expirationTime - Date.now() - 10 * 60 * 1000; // 10分前

  // 残り時間ありの場合クリア
  if (refreshTimeoutRef) {
    clearTimeout(refreshTimeoutRef);
  }

  // Refreshtimeが正の値の場合、タイマーを設定。
  if (refreshTime > 0) {
    refreshTimeoutRef = setTimeout(() => {
      onRefresh();
    }, refreshTime);
  } else {
    logToCloudWatch('[setBiTokenRefreshTimer] refreshTimeが負の値のため即時更新。','FRONTEND_ERROR','WARNING');
    //console.warn('');
    onRefresh();
  }
};
